import React, { useState, useEffect } from 'react';
import { staffAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Input from '../../components/common/Input';
 

const StaffForm = ({ staff, departments, onSave, onCancel }) => {
  const [tab, setTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', first_name: '', last_name: '', date_of_birth: '',
    gender: 'M', staff_type: 'teacher', department: '', designation: '',
    qualification: '', specialization: '', experience_years: 0,
    joining_date: new Date().toISOString().split('T')[0], status: 'active',
    email: '', phone: '', address: '', emergency_contact: '',
    basic_salary: 0, bank_account: '', bank_name: '', ifsc_code: '', pan_number: '',
  });

  useEffect(() => {
    if (staff) setForm(prev => ({ ...prev, ...staff }));
  }, [staff]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (staff) await staffAPI.update(staff.id, form);
      else await staffAPI.create(form);
      toast.success(staff ? 'Staff updated!' : 'Staff added!');
      onSave();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.entries(errors).forEach(([k, v]) => toast.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      else toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  const Field = ({ label, name, options, required, col = 4 }) => (
    <div className={`col-md-${col} mb-3`}>
      <label className="form-label">{label}{required && <span className="text-danger ms-1">*</span>}</label>
      <select className="form-select" name={name} value={form[name] || ''} onChange={handleChange} required={required}>
        <option value="">Select {label}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex gap-2 mb-4 border-bottom pb-3 flex-wrap">
        {[['personal', '👤 Personal'], ['professional', '💼 Professional'], ['contact', '📞 Contact']].map(([k, l]) => (
          <button key={k} type="button"
            className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setTab(k)}>{l}
          </button>
        ))}
      </div>

      <div className="row">
        {tab === 'personal' && <>
          <div className="col-md-4">
            <Input label="Employee ID" name="employee_id" type="text" value={form.employee_id} onChange={handleChange} required />
          </div>
          <div className="col-md-4">
            <Input label="First Name" name="first_name" type="text" value={form.first_name} onChange={handleChange} required />
          </div>
          <div className="col-md-4">
            <Input label="Last Name" name="last_name" type="text" value={form.last_name} onChange={handleChange} required />
          </div>
          <div className="col-md-4">
            <Input label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} required />
          </div>
          <Field label="Gender" name="gender" options={[{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }]} required />
          <Field label="Status" name="status" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'on_leave', label: 'On Leave' }]} />
        </>}

        {tab === 'professional' && <>
          <Field label="Staff Type" name="staff_type" options={[{ value: 'teacher', label: 'Teacher' }, { value: 'admin', label: 'Admin' }, { value: 'support', label: 'Support' }, { value: 'management', label: 'Management' }]} required />
          <Field label="Department" name="department" options={departments.map(d => ({ value: d.id, label: d.name }))} />
          <div className="col-md-4">
            <Input label="Designation" name="designation" type="text" value={form.designation} onChange={handleChange} required />
          </div>
          <div className="col-md-4">
            <Input label="Qualification" name="qualification" type="text" value={form.qualification} onChange={handleChange} required />
          </div>
          <div className="col-md-4">
            <Input label="Specialization" name="specialization" type="text" value={form.specialization} onChange={handleChange} />
          </div>
          <div className="col-md-4">
            <Input label="Experience (Years)" name="experience_years" type="number" value={form.experience_years} onChange={handleChange} />
          </div>
          <div className="col-md-4">
            <Input label="Joining Date" name="joining_date" type="date" value={form.joining_date} onChange={handleChange} required />
          </div>
        </>}

        {tab === 'contact' && <>
          <div className="col-md-6">
            <Input label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <Input label="Phone" name="phone" type="text" value={form.phone} onChange={handleChange} />
          </div>
          <div className="col-12 mb-3">
            <label className="form-label">Address</label>
            <textarea className="form-control" name="address" value={form.address || ''} onChange={handleChange} rows={3} />
          </div>
          <div className="col-md-6">
            <Input label="Emergency Contact" name="emergency_contact" type="text" value={form.emergency_contact} onChange={handleChange} />
          </div>
        </>}
      </div>

      <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : staff ? '💾 Update' : '➕ Add Staff'}
        </button>
      </div>
    </form>
  );
};

export default StaffForm;